import { useMemo } from "react"
import { Menu, MenuItem, Popover } from "@blueprintjs/core"
import { SegmentButton, SelectorContainer } from "../Factsheet.styled"
import { GroupingDimension } from "../types"
import useTagGroups from "../hooks/useTagGroups"

interface GroupSelectorProps {
  selected: GroupingDimension
  onChange: (dimension: GroupingDimension) => void
}

const BASE_DIMENSIONS: { value: GroupingDimension; label: string }[] = [
  { value: "sector_l1", label: "Sector L1" },
  { value: "sector_l2", label: "Sector L2" },
  { value: "sector_l3", label: "Sector L3" },
  { value: "country_code", label: "Country" },
]

const GroupSelector = ({ selected, onChange }: GroupSelectorProps) => {
  const { data: tagGroups } = useTagGroups()

  const selectedTagGroup = useMemo(() => {
    if (!selected.startsWith("tag_") || !tagGroups) return null
    const id = Number(selected.slice(4))
    return tagGroups.find((g) => g.id === id) ?? null
  }, [selected, tagGroups])

  const isTagSelected = selected.startsWith("tag_")

  const tagMenu = useMemo(
    () => (
      <Menu>
        {(tagGroups ?? []).map((group) => (
          <MenuItem
            key={group.id}
            text={group.name}
            label={group.owner_username}
            active={selected === `tag_${group.id}`}
            onClick={() => onChange(`tag_${group.id}` as GroupingDimension)}
          />
        ))}
        {(!tagGroups || tagGroups.length === 0) && (
          <MenuItem text="No tag groups" disabled />
        )}
      </Menu>
    ),
    [tagGroups, selected, onChange],
  )

  return (
    <SelectorContainer>
      {BASE_DIMENSIONS.map(({ value, label }) => (
        <SegmentButton key={value} isActive={selected === value} onClick={() => onChange(value)}>
          {label}
        </SegmentButton>
      ))}
      <Popover content={tagMenu} placement="bottom-start" minimal>
        <SegmentButton isActive={isTagSelected}>
          {selectedTagGroup ? selectedTagGroup.name : "Tags"} ▾
        </SegmentButton>
      </Popover>
    </SelectorContainer>
  )
}

export default GroupSelector
